import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pencil, Megaphone, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", meeting_date: "", agenda: "", minutes: "", decisions: "", attendees: [], signed_off: false };

export default function Meetings() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const canManage = ["super_admin", "league_admin"].includes(user.role);

  const load = async () => { const { data } = await api.get("/meetings"); setItems(data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ new: true }); setForm({ ...EMPTY, meeting_date: new Date().toISOString().slice(0, 10) }); };
  const startEdit = (m) => { setEditing(m); setForm({ ...EMPTY, ...m, attendees: m.attendees || [] }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.title || !form.meeting_date) return toast.error("Title and date required");
    try {
      if (editing.new) await api.post("/meetings", form);
      else await api.put(`/meetings/${editing.id}`, form);
      toast.success("Saved");
      cancel();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete meeting?")) return;
    await api.delete(`/meetings/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Module 3 · Governance</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Owners Meetings</h1>
          <div className="text-xs text-zinc-400 mt-1">Legally binding decisions log across all teams</div>
        </div>
        {canManage && (
          <Button data-testid="btn-new-meeting" onClick={startNew} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">
            <Plus className="w-4 h-4 mr-1.5" /> New Meeting
          </Button>
        )}
      </div>

      {editing && (
        <Card className="p-6 bg-[#11241b] border-white/10 mb-6" data-testid="meeting-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *"><Input data-testid="meeting-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Date *"><Input data-testid="meeting-date" type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Attendees (comma-separated)" full>
              <Input data-testid="meeting-attendees" value={(form.attendees || []).join(", ")} onChange={(e) => setForm({ ...form, attendees: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="bg-[#060d09] border-white/10 text-white" />
            </Field>
            <Field label="Agenda" full><Textarea data-testid="meeting-agenda" rows={3} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Minutes" full><Textarea data-testid="meeting-minutes" rows={5} value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <Field label="Decisions (binding)" full><Textarea data-testid="meeting-decisions" rows={4} value={form.decisions} onChange={(e) => setForm({ ...form, decisions: e.target.value })} className="bg-[#060d09] border-white/10 text-white" /></Field>
            <div className="flex items-center gap-2 md:col-span-2">
              <Switch data-testid="meeting-signed" checked={form.signed_off} onCheckedChange={(v) => setForm({ ...form, signed_off: v })} />
              <Label className="text-zinc-300">Signed Off by all owners (decisions become legally binding)</Label>
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <Button data-testid="btn-save-meeting" onClick={save} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold">Save</Button>
            <Button onClick={cancel} variant="outline" className="border-white/20 text-white">Cancel</Button>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {items.map((m) => (
          <Card key={m.id} className="p-5 bg-[#11241b] border-white/10" data-testid={`meeting-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.meeting_date} · {(m.attendees || []).length} attendees</div>
                <div className="font-anton text-2xl uppercase tracking-tight text-white">{m.title}</div>
                {m.signed_off && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-[#00A86B]/15 border border-[#00A86B]/40 text-[#00A86B] text-[10px] uppercase tracking-widest">
                    <FileCheck2 className="w-3 h-3" /> Signed Off — Binding
                  </span>
                )}
              </div>
              {canManage && (
                <div className="flex gap-1">
                  <Button data-testid={`btn-edit-meeting-${m.id}`} size="sm" variant="outline" onClick={() => startEdit(m)} className="border-white/10"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`btn-del-meeting-${m.id}`} size="sm" variant="outline" onClick={() => del(m.id)} className="border-red-500/30 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              )}
            </div>
            {m.agenda && <Section label="Agenda" text={m.agenda} />}
            {m.minutes && <Section label="Minutes" text={m.minutes} />}
            {m.decisions && <Section label="Decisions" text={m.decisions} highlight />}
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-zinc-500 py-12" data-testid="meetings-empty">No meetings logged yet.</div>}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Section({ label, text, highlight }) {
  return (
    <div className="mt-3">
      <div className={`text-[10px] uppercase tracking-widest mb-1 ${highlight ? "text-[#00A86B]" : "text-zinc-500"}`}>{label}</div>
      <div className={`whitespace-pre-line text-sm ${highlight ? "text-white" : "text-zinc-300"}`}>{text}</div>
    </div>
  );
}
